import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams, useNavigate } from 'react-router-dom';
import useToggleSidebar from "../../../components/togglesidebar";
import Sidebar from '../../sidebar';
import Header from '../../Header';
import Footer from '../../footer';
import api from "../../../services/api";
import API_URL, { buildImageUrl } from "../../../config/config";
import ImageCropModal from "../../../components/ImageCropModal";

// helper: convert title to URL-friendly slug
// helper: convert title to URL-friendly slug (keeps multi-word, removes punctuation)
const slugify = (text = '') => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/&/g, '-and-')          // handle '&'
    .replace(/[^\w\s-]/g, '')        // remove all non-word chars except spaces and hyphens
    .replace(/\s+/g, '-')           // replace spaces with hyphens
    .replace(/-+/g, '-')            // collapse multiple hyphens
    .replace(/^-+|-+$/g, '');       // trim leading/trailing hyphens
};


function AddBlogs() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isSidebarOpen, toggleSidebar } = useToggleSidebar();

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        url: "",
        status: "active",
        image: null,
        existing_image_url: "",
        tag: "",
        short_desc: "",
        meta_desc: "",
        h1_tag: "",
        h2_tag: "",
        bold_tag: "",
        italic_tag: "",
        blog_url: "",
        image_alt: ""
    });

    const [formErrors, setFormErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [backendMessage, setBackendMessage] = useState({ type: '', text: '' });
    const [userEditedSlug, setUserEditedSlug] = useState(false);
    const [metaKeywordsEdited, setMetaKeywordsEdited] = useState(false);
    const [imageAltEdited, setImageAltEdited] = useState(false);
    const [h1Edited, setH1Edited] = useState(false);
    const [h2Edited, setH2Edited] = useState(false);
    const editorRef = useRef(null);
    const editorInstanceRef = useRef(null);
    const imageInputRef = useRef(null);

    const descriptionRef = useRef("");

    useEffect(() => {
        descriptionRef.current = formData.description;
    }, [formData.description]);

    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState("");
    const [cropFileName, setCropFileName] = useState("");
    const [cropFileType, setCropFileType] = useState("");

    useEffect(() => {
        const loadCkeditor = async () => {
            if (window.ClassicEditor === undefined) {
                const script = document.createElement('script');
                script.src = "https://cdn.ckeditor.com/ckeditor5/37.0.1/classic/ckeditor.js";
                script.onload = () => {
                    initCkeditor();
                };
                document.body.appendChild(script);
            } else {
                initCkeditor();
            }
        };

        const initCkeditor = () => {
            if (!editorRef.current) {
                setTimeout(initCkeditor, 100); // Retry shortly
                return;
            }
            if (editorRef.current && !editorInstanceRef.current) {
                window.ClassicEditor
                    .create(editorRef.current)
                    .then(editor => {
                        editorInstanceRef.current = editor;
                        // Only set data if not editing (id is null) or if data is already loaded
                        if (!id || descriptionRef.current) {
                            editor.setData(descriptionRef.current || '');
                        }
                        editor.model.document.on('change:data', () => {
                            setFormData(prev => ({
                                ...prev,
                                description: editor.getData()
                            }));
                            setFormErrors(prev => (prev.description ? { ...prev, description: "" } : prev));
                        });
                    })
                    .catch(error => {
                        console.error("Error initializing CKEditor:", error);
                    });
            }
        };

        const fetchBlogData = async () => {
            if (id) {
                try {
                    console.log("Fetching blog data for ID:", id);
                    const response = await api.get(`/bloglist/${id}`);
                    console.log("Blog API Response:", response.data);

                    const blogData = response.data.item || response.data.blog || response.data;
                    console.log("Extracted blog data:", blogData);

                    // Map backend fields to frontend form fields with explicit mapping
                    const mappedFormData = {
                        title: blogData.title || "",
                        description: blogData.content || blogData.description || "",
                        url: blogData.slug || blogData.url || "",
                        status: blogData.featured ? "featured" : (blogData.status || "active"),
                        image: null,
                        existing_image_url: blogData.image || blogData.image_url || "",
                        tag: Array.isArray(blogData.tags) ? blogData.tags.join(", ") : (blogData.tag || blogData.tags || ""),
                        short_desc: blogData.excerpt || blogData.short_desc || "",
                        meta_desc: blogData.meta_desc || blogData.metaDescription || "",
                        h1_tag: blogData.h1_tag || blogData.title || "",
                        h2_tag: blogData.h2_tag || blogData.meta_desc || blogData.metaDescription || "",
                        bold_tag: blogData.bold_tag || "",
                        italic_tag: blogData.italic_tag || "",
                        blog_url: blogData.slug || blogData.blog_url || "",
                        image_alt: blogData.image_alt || ""
                    };

                    const normalizedTag = String(mappedFormData.tag || "").trim();
                    const normalizedAlt = String(mappedFormData.image_alt || "").trim();

                    // If one is missing, default to the other so they match by default.
                    if (normalizedTag && !normalizedAlt) mappedFormData.image_alt = normalizedTag;
                    if (!normalizedTag && normalizedAlt) mappedFormData.tag = normalizedAlt;

                    setMetaKeywordsEdited(false);
                    setImageAltEdited(false);
                    setH1Edited(Boolean(mappedFormData.h1_tag && mappedFormData.title && mappedFormData.h1_tag !== mappedFormData.title));
                    setH2Edited(Boolean(mappedFormData.h2_tag && mappedFormData.meta_desc && mappedFormData.h2_tag !== mappedFormData.meta_desc));

                    console.log("Mapped form data:", mappedFormData);
                    setFormData(mappedFormData);

                    // Wait for CKEditor to be ready, then set data
                    const setEditorData = () => {
                        if (editorInstanceRef.current) {
                            const content = mappedFormData.description || blogData.content || blogData.description || '';
                            console.log("Setting CKEditor content:", content);
                            editorInstanceRef.current.setData(content);
                        } else {
                            let retries = 0;
                            const trySetData = () => {
                                if (editorInstanceRef.current) {
                                    const content = mappedFormData.description || blogData.content || blogData.description || '';
                                    editorInstanceRef.current.setData(content);
                                } else if (retries < 10) {
                                    retries++;
                                    setTimeout(trySetData, 200);
                                }
                            };
                            setTimeout(trySetData, 200);
                        }
                    };
                    setEditorData();

                } catch (err) {
                    console.error("Error fetching blog data for edit:", err.response?.data || err.message);
                    setBackendMessage({ type: 'danger', text: 'Failed to load blog data for editing.' });
                }
            } else {
                if (editorInstanceRef.current) {
                    editorInstanceRef.current.setData('');
                }
                setFormData({
                    title: "",
                    description: "",
                    url: "",
                    status: "active",
                    image: null,
                    existing_image_url: "",
                    tag: "",
                    short_desc: "",
                    meta_desc: "",
                    h1_tag: "",
                    h2_tag: "",
                    bold_tag: "",
                    italic_tag: "",
                    blog_url: "",
                    image_alt: ""
                });
                setMetaKeywordsEdited(false);
                setImageAltEdited(false);
                setH1Edited(false);
                setH2Edited(false);
            }
            setLoading(false);
        };

        // Fetch data first if editing, then load CKEditor
        if (id) {
            fetchBlogData();
        } else {
            setLoading(false);
        }

        loadCkeditor();

        return () => {
            if (editorInstanceRef.current) {
                editorInstanceRef.current.destroy()
                    .catch(error => console.error('Error destroying CKEditor:', error));
                editorInstanceRef.current = null;
            }
        };
    }, [id]);

    // Update editor when formData.description changes (to keep them in sync)
    useEffect(() => {
        if (editorInstanceRef.current && id && formData.description) {
            const currentContent = editorInstanceRef.current.getData();
            if (currentContent !== formData.description) {
                editorInstanceRef.current.setData(formData.description);
            }
        }
    }, [formData.description, id]);

    const handleChange = (e) => {
        const { name, value, type } = e.target;

        // Title auto-fills slug/blog_url and H1 (only for new posts or when slug is empty)
        if (name === 'title') {
            const newSlug = slugify(value);
            setFormData(prev => ({
                ...prev,
                title: value,
                url: (!id && !userEditedSlug) ? newSlug : prev.url,
                blog_url: (!id && !userEditedSlug) ? newSlug : prev.blog_url,
                h1_tag: h1Edited ? prev.h1_tag : value
            }));
            if (formErrors.title) setFormErrors(prev => ({ ...prev, title: "" }));
            return;
        }

        if (name === 'h1_tag') {
            setH1Edited(true);
            setFormData(prev => ({ ...prev, h1_tag: value }));
            if (formErrors.h1_tag) setFormErrors(prev => ({ ...prev, h1_tag: "" }));
            return;
        }

        // Meta description mirrors into h2_tag
        if (name === 'meta_desc') {
            setFormData(prev => ({
                ...prev,
                meta_desc: value,
                h2_tag: h2Edited ? prev.h2_tag : value
            }));
            if (formErrors.meta_desc) setFormErrors(prev => ({ ...prev, meta_desc: "" }));
            return;
        }

        if (name === 'h2_tag') {
            setH2Edited(true);
            setFormData(prev => ({ ...prev, h2_tag: value }));
            if (formErrors.h2_tag) setFormErrors(prev => ({ ...prev, h2_tag: "" }));
            return;
        }

        // Meta keywords and image alt are synced by default unless both are manually edited
        if (name === 'tag') {
            setMetaKeywordsEdited(true);
            setFormData(prev => ({
                ...prev,
                tag: value,
                image_alt: imageAltEdited ? prev.image_alt : value
            }));
            if (formErrors.tag) setFormErrors(prev => ({ ...prev, tag: "" }));
            return;
        }

        if (name === 'image_alt') {
            setImageAltEdited(true);
            setFormData(prev => ({
                ...prev,
                image_alt: value,
                tag: metaKeywordsEdited ? prev.tag : value
            }));
            if (formErrors.image_alt) setFormErrors(prev => ({ ...prev, image_alt: "" }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'file' ? prev[name] : value
        }));

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const previewImage = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = function (ev) {
                const nextSrc = typeof ev?.target?.result === "string" ? ev.target.result : "";
                setCropImageSrc(nextSrc);
                setCropFileName(file.name || "image");
                setCropFileType(file.type || "image/jpeg");
                setCropModalOpen(true);
            };
            reader.readAsDataURL(file);
        } else {
            setFormData(prev => ({
                ...prev,
                image: null,

                existing_image_url: id ? prev.existing_image_url : "",
                image_alt: imageAltEdited ? prev.image_alt : ''
            }));
        }
        if (formErrors.image) {
            setFormErrors(prev => ({ ...prev, image: "" }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setFormErrors({});
        setBackendMessage({ type: '', text: '' });

        if (!id) {
            const errors = {};
            if (!formData.title.trim()) errors.title = "Title is required";
            if (!formData.image) errors.image = "Please upload an image";
            if (!formData.status) errors.status = "Status is required";

            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                setSubmitting(false);
                return;
            }
        }

        // Build FormData explicitly and safely
        const dataToSend = new FormData();
        dataToSend.append('title', formData.title || '');
        dataToSend.append('description', editorInstanceRef.current ? editorInstanceRef.current.getData() : formData.description || '');
        dataToSend.append('url', formData.url || '');
        dataToSend.append('blog_url', formData.blog_url || '');
        dataToSend.append('status', formData.status || '');
        const tagValue = String(formData.tag || '').trim();
        const imageAltValue = (!id && !imageAltEdited && tagValue)
            ? tagValue
            : String(formData.image_alt || '');

        dataToSend.append('tag', tagValue);
        dataToSend.append('short_desc', formData.short_desc || '');
        dataToSend.append('meta_desc', formData.meta_desc || '');
        dataToSend.append('h1_tag', formData.h1_tag || '');
        dataToSend.append('h2_tag', formData.h2_tag || '');
        dataToSend.append('bold_tag', formData.bold_tag || '');
        dataToSend.append('italic_tag', formData.italic_tag || '');
        dataToSend.append('image_alt', imageAltValue);

        if (formData.image) {
            dataToSend.append('image', formData.image);
        } else if (id && formData.existing_image_url) {
            dataToSend.append('existing_image_url', formData.existing_image_url);
        }

        try {
            if (id) {
                await api.patch(`/bloglist/update/${id}`, dataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setBackendMessage({ type: 'success', text: 'Blog updated successfully!' });
            } else {
                await api.post(`/bloglist/store`, dataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setBackendMessage({ type: 'success', text: 'Blog added successfully!' });
                setFormData({
                    title: "",
                    description: "",
                    url: "",
                    status: "active",
                    image: null,
                    existing_image_url: "",
                    tag: "",
                    short_desc: "",
                    meta_desc: "",
                    h1_tag: "",
                    h2_tag: "",
                    bold_tag: "",
                    italic_tag: "",
                    blog_url: "",
                    image_alt: ""
                });
                if (editorInstanceRef.current) {
                    editorInstanceRef.current.setData('');
                }
            }
            setTimeout(() => navigate('/admin/bloglist'), 1500);
        } catch (err) {
            console.error("Submission error:", err.response?.data || err.message);
            if (err.response && err.response.status === 422 && err.response.data.errors) {
                const newErrors = {};
                if (Array.isArray(err.response.data.errors)) {
                    err.response.data.errors.forEach(error => {
                        newErrors[error.param] = error.msg;
                    });
                } else {
                    Object.assign(newErrors, err.response.data.errors);
                }
                setFormErrors(newErrors);
                setBackendMessage({ type: 'danger', text: 'Validation failed. Please check your inputs.' });
            } else {
                setBackendMessage({ type: 'danger', text: err.response?.data?.error || 'An unexpected error occurred.' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s", padding: "20px" }}>
                <div className="card">
                    <div className="card-header">Loading...</div>
                    <div className="card-body">
                        <div className="row">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <div key={i} className={`form-group ${i === 0 || i === 1 || i === 2 || i === 3 || i === 4 || i === 5 || i === 7 || i === 8 || i === 9 || i === 10 || i === 11 ? 'col-sm-6' : 'col-sm-12'}`}>
                                    <div className="skeletant-bx skeletant-design sh-47" style={{ height: '30px', marginBottom: '10px' }}></div>
                                    <div className="skeletant-bx skeletant-design sh-47" style={{ height: (i === 6 ? '100px' : '40px') }}></div>
                                </div>
                            ))}
                            <div className="col-sm-12 mt-3">
                                <div className="skeletant-bx skeletant-design sh-47" style={{ height: '40px', width: '150px' }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // helper to produce preview src (use buildImageUrl for server path, respect data URLs)
    const previewSrc = (existing) => {
        if (!existing) return null;
        if (existing.startsWith('data:')) return existing;
        try {
            return buildImageUrl ? buildImageUrl(existing) : `${API_URL}/${existing}`;
        } catch {
            return existing;
        }
    };

    return (
        <>
            <Helmet>
                <title>Admin | {id ? "Edit Blog Details" : "Add Blog Details"}</title>
            </Helmet>
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
                <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
                    <Header toggleSidebar={toggleSidebar} />
                    <section className='content'>
                        <div className="breadcrumb-main-bx">
                            <div className="breadcrumb-bx">
                                <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <Link className="breadcrumb-link" to="/admin/bloglist">Blog</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <div className="breadcrumb-link breadcrumb-active">{id ? "Edit Blog" : "Add Blog"}</div>
                            </div>
                        </div>

                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card">
                                        <div className="card-header">{id ? "Edit Blog" : "Add Blog"}</div>
                                        <div className="card-body">
                                            {backendMessage.text && (
                                                <div className={`alert alert-${backendMessage.type === 'success' ? 'success' : 'danger'}`} role="alert">
                                                    {backendMessage.text}
                                                </div>
                                            )}

                                            <form onSubmit={handleSubmit} encType="multipart/form-data">
                                                <div className="row">
                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="title">Title</label>
                                                        <input type="text" className={`form-control ${formErrors.title ? 'is-invalid' : ''}`} id="title" placeholder="Enter title" name="title" value={formData.title} onChange={handleChange} />
                                                        {formErrors.title && <div className="invalid-feedback">{formErrors.title}</div>}
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="url">Url (slug)</label>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                          <input
                                                            type="text"
                                                            className={`form-control ${formErrors.url ? 'is-invalid' : ''}`}
                                                            id="url"
                                                            placeholder="Enter url"
                                                            name="url"
                                                            value={formData.url}
                                                            onChange={(e) => {
                                                                setUserEditedSlug(true);
                                                                setFormData(prev => ({ ...prev, url: e.target.value }));
                                                            }}
                                                          />

                                                            {/* <button type="button" className="btn btn-outline-secondary" onClick={() => setFormData(prev => ({ ...prev, url: slugify(prev.title) }))}>Regenerate</button> */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            onClick={() => {
                                                                const newSlug = slugify(formData.title || '');
                                                                setFormData(prev => ({ ...prev, url: newSlug }));
                                                                setUserEditedSlug(false);
                                                            }}
                                                        >
                                                            Regenerate
                                                        </button>

                                                        </div>
                                                        {formErrors.url && <div className="invalid-feedback">{formErrors.url}</div>}
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="tag">Meta Keywords</label>
                                                        <input type="text" className="form-control" id="tag" placeholder="Enter Tag (comma separated)" name="tag" value={formData.tag} onChange={handleChange} />
                                                        <small className="form-text text-muted">If left empty, image alt text will be used as default.</small>
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="short_desc">Short Description</label>
                                                        <input type="text" className="form-control" id="short_desc" placeholder="Enter short description" name="short_desc" value={formData.short_desc} onChange={(e) => setFormData(prev => ({ ...prev, short_desc: e.target.value }))} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="image">Image</label>
                                                        <input ref={imageInputRef} type="file" className={`form-control ${formErrors.image ? 'is-invalid' : ''}`} id="image" name="image" onChange={previewImage} accept="image/jpeg,image/png,image/jpg" />
                                                        <div className="input-note-2 mt-1">
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"></path></svg>
                                                        </div>
                                                        {formErrors.image && <div className="invalid-feedback">{formErrors.image}</div>}
                                                        {formData.existing_image_url && (
                                                            <img src={previewSrc(formData.existing_image_url)} alt="Preview" style={{ maxWidth: '100px', marginTop: '10px', display: 'block' }} />
                                                        )}
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="status">Status</label>
                                                        <select className={`form-control ${formErrors.status ? 'is-invalid' : ''}`} id="status" name="status" value={formData.status} onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}>
                                                            <option value="">Select Status</option>
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                            <option value="draft">Draft</option>
                                                            <option value="featured">Featured</option>
                                                        </select>
                                                        {formErrors.status && <div className="invalid-feedback">{formErrors.status}</div>}
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label htmlFor="description">Description</label>
                                                        <textarea ref={editorRef} className={`form-control ${formErrors.description ? 'is-invalid' : ''}`} id="description" placeholder="Enter Description" name="description"></textarea>
                                                        {formErrors.description && <div className="invalid-feedback">{formErrors.description}</div>}
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label htmlFor="meta_desc">Meta Description</label>
                                                        <input type="text" className="form-control" id="meta_desc" placeholder="Enter meta description" name="meta_desc" value={formData.meta_desc} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="h1_tag">H1 Tags (Page title)</label>
                                                        <input type="text" className="form-control" id="h1_tag" placeholder="Enter H1 tags" name="h1_tag" value={formData.h1_tag} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="h2_tag">H2 Tags (Meta description)</label>
                                                        <input type="text" className="form-control" id="h2_tag" placeholder="Enter H2 tags" name="h2_tag" value={formData.h2_tag} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="image_alt">Image Alt Text (used as meta keywords if tag empty)</label>
                                                        <input type="text" className="form-control" id="image_alt" placeholder="Enter image alt text" name="image_alt" value={formData.image_alt} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="bold_tag">Bold Tags</label>
                                                        <input type="text" className="form-control" id="bold_tag" placeholder="Enter bold tags" name="bold_tag" value={formData.bold_tag} onChange={(e) => setFormData(prev => ({ ...prev, bold_tag: e.target.value }))} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="italic_tag">Italic Tags</label>
                                                        <input type="text" className="form-control" id="italic_tag" placeholder="Enter italic tags" name="italic_tag" value={formData.italic_tag} onChange={(e) => setFormData(prev => ({ ...prev, italic_tag: e.target.value }))} />
                                                    </div>
                                                </div>

                                                <button type="submit" className="common-submit-btn mt-2" disabled={submitting}>
                                                    {submitting ? 'Submitting...' : (id ? 'Update Blog' : 'Add Blog')}
                                                </button>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>

            <ImageCropModal
                isOpen={cropModalOpen}
                imageSrc={cropImageSrc}
                fileName={cropFileName}
                fileType={cropFileType}
                aspect={16 / 9}
                onClose={() => {
                    setCropModalOpen(false);
                    setCropImageSrc("");
                    if (imageInputRef.current) imageInputRef.current.value = "";
                }}
                onCropped={async ({ file, previewUrl }) => {
                    const filename = String(file?.name || "image")
                        .replace(/\.[^/.]+$/, "")
                        .replace(/[-_]+/g, " ");
                    setFormData((prev) => ({
                        ...prev,
                        image: file,
                        existing_image_url: previewUrl,
                        image_alt: imageAltEdited
                            ? prev.image_alt
                            : metaKeywordsEdited
                                ? prev.tag || filename
                                : filename,
                        tag: metaKeywordsEdited ? prev.tag : prev.tag ? prev.tag : filename,
                    }));
                    if (imageInputRef.current) imageInputRef.current.value = "";
                }}
            />
        </>
    );
}

export default AddBlogs;
//old 
// import React, { useState, useEffect, useRef } from "react";
// import { Helmet } from "react-helmet";
// import { Link, useParams, useNavigate } from 'react-router-dom';
// import useToggleSidebar from "../../../components/togglesidebar";
// import Sidebar from '../../sidebar';
// import Header from '../../Header';
// import Footer from '../../footer';
// import api from "../../../services/api";
// import API_URL, { buildImageUrl } from "../../../config/config";

// function AddBlogs() {
//     const { id } = useParams();
//     const navigate = useNavigate();
//     const { isSidebarOpen, toggleSidebar } = useToggleSidebar();

//     const [formData, setFormData] = useState({
//         title: "",
//         description: "",
//         url: "",
//         status: "active",
//         image: null,
//         existing_image_url: "",
//         tag: "",
//         short_desc: "",
//         meta_desc: "",
//         h1_tag: "",
//         h2_tag: "",
//         bold_tag: "",
//         italic_tag: "",
//         blog_url: ""
//     });

//     const [formErrors, setFormErrors] = useState({});
//     const [loading, setLoading] = useState(true);
//     const [submitting, setSubmitting] = useState(false);
//     const [backendMessage, setBackendMessage] = useState({ type: '', text: '' });

//     const editorRef = useRef(null);
//     const editorInstanceRef = useRef(null);

//     useEffect(() => {
//         const loadCkeditor = async () => {
//             if (window.ClassicEditor === undefined) {
//                 const script = document.createElement('script');
//                 script.src = "https://cdn.ckeditor.com/ckeditor5/37.0.1/classic/ckeditor.js";
//                 script.onload = () => {
//                     initCkeditor();
//                 };
//                 document.body.appendChild(script);
//             } else {
//                 initCkeditor();
//             }
//         };

//         const initCkeditor = () => {
//             if (!editorRef.current) {
//                 setTimeout(initCkeditor, 100); // Retry shortly
//                 return;
//             }
//             if (editorRef.current && !editorInstanceRef.current) {
//                 window.ClassicEditor
//                     .create(editorRef.current)
//                     .then(editor => {
//                         editorInstanceRef.current = editor;
//                         // Only set data if not editing (id is null) or if data is already loaded
//                         if (!id || formData.description) {
//                             editor.setData(formData.description || '');
//                         }
//                         editor.model.document.on('change:data', () => {
//                             setFormData(prev => ({
//                                 ...prev,
//                                 description: editor.getData()
//                             }));
//                             if (formErrors.description) {
//                                 setFormErrors(prev => ({ ...prev, description: "" }));
//                             }
//                         });
//                     })
//                     .catch(error => {
//                         console.error("Error initializing CKEditor:", error);
//                     });
//             }
//         };

//         const fetchBlogData = async () => {
//             if (id) {
//                 try {
//                     console.log("Fetching blog data for ID:", id);
//                     const response = await api.get(`/bloglist/${id}`);
//                     console.log("Blog API Response:", response.data);
                    
//                     const blogData = response.data.item || response.data.blog || response.data;
//                     console.log("Extracted blog data:", blogData);

//                     // Map backend fields to frontend form fields
//                     // Backend: title, slug, excerpt, content, author, publishDate, readTime, category, tags (array), image, featured
//                     // Frontend: title, description (content), url (slug), status (featured), image, existing_image_url (image), tag (tags), short_desc (excerpt), meta_desc, h1_tag, h2_tag, bold_tag, italic_tag, blog_url (slug)
                    
//                     const mappedFormData = {
//                         title: blogData.title || "",
//                         description: blogData.content || blogData.description || "", // Map content to description
//                         url: blogData.slug || blogData.url || "", // Map slug to url
//                         status: blogData.featured ? "featured" : (blogData.status || "active"), // Map featured to status
//                         image: null, // Always null for file input
//                         existing_image_url: blogData.image || blogData.image_url || "", // Map image to existing_image_url
//                         tag: Array.isArray(blogData.tags) ? blogData.tags.join(", ") : (blogData.tag || blogData.tags || ""), // Convert tags array to comma-separated string
//                         short_desc: blogData.excerpt || blogData.short_desc || "", // Map excerpt to short_desc
//                         meta_desc: blogData.meta_desc || "", // Not in backend model, keep empty or add to model
//                         h1_tag: blogData.h1_tag || "", // Not in backend model, keep empty or add to model
//                         h2_tag: blogData.h2_tag || "", // Not in backend model, keep empty or add to model
//                         bold_tag: blogData.bold_tag || "", // Not in backend model, keep empty or add to model
//                         italic_tag: blogData.italic_tag || "", // Not in backend model, keep empty or add to model
//                         blog_url: blogData.slug || blogData.blog_url || "" // Map slug to blog_url
//                     };
                    
//                     console.log("Mapped form data:", mappedFormData);
//                     setFormData(mappedFormData);

//                     // Wait for CKEditor to be ready, then set data
//                     const setEditorData = () => {
//                         if (editorInstanceRef.current) {
//                             const content = mappedFormData.description || blogData.content || blogData.description || '';
//                             console.log("Setting CKEditor content:", content);
//                             editorInstanceRef.current.setData(content);
//                         } else {
//                             // Retry after a short delay if editor is not ready (max 10 retries)
//                             let retries = 0;
//                             const trySetData = () => {
//                                 if (editorInstanceRef.current) {
//                                     const content = mappedFormData.description || blogData.content || blogData.description || '';
//                                     editorInstanceRef.current.setData(content);
//                                 } else if (retries < 10) {
//                                     retries++;
//                                     setTimeout(trySetData, 200);
//                                 }
//                             };
//                             setTimeout(trySetData, 200);
//                         }
//                     };
//                     setEditorData();

//                 } catch (err) {
//                     console.error("Error fetching blog data for edit:", err.response?.data || err.message);
//                     setBackendMessage({ type: 'danger', text: 'Failed to load blog data for editing.' });
//                 }
//             } else {
//                 if (editorInstanceRef.current) {
//                     editorInstanceRef.current.setData('');
//                 }
//                 setFormData({
//                     title: "",
//                     description: "",
//                     url: "",
//                     status: "active",
//                     image: null,
//                     existing_image_url: "",
//                     tag: "",
//                     short_desc: "",
//                     meta_desc: "",
//                     h1_tag: "",
//                     h2_tag: "",
//                     bold_tag: "",
//                     italic_tag: "",
//                     blog_url: ""
//                 });
//             }
//             setLoading(false);
//         };

//         // Fetch data first if editing, then load CKEditor
//         if (id) {
//             fetchBlogData();
//         } else {
//             setLoading(false);
//         }
        
//         // Initialize CKEditor (will be populated after data loads)
//         loadCkeditor();

//         return () => {
//             if (editorInstanceRef.current) {
//                 editorInstanceRef.current.destroy()
//                     .catch(error => console.error('Error destroying CKEditor:', error));
//                 editorInstanceRef.current = null;
//             }
//         };
//     }, [id]);
    
//     // Separate effect to update CKEditor when formData.description changes (after initial load)
//     useEffect(() => {
//         if (editorInstanceRef.current && id && formData.description) {
//             // Only update if content is different to avoid infinite loops
//             const currentContent = editorInstanceRef.current.getData();
//             if (currentContent !== formData.description) {
//                 editorInstanceRef.current.setData(formData.description);
//             }
//         }
//     }, [formData.description, id]);

//     const handleChange = (e) => {
//         const { name, value, type, files } = e.target;
//         setFormData(prev => ({
//             ...prev,
//             [name]: type === 'file' ? files[0] : value
//         }));

//         if (formErrors[name]) {
//             setFormErrors(prev => ({ ...prev, [name]: "" }));
//         }
//     };

//     const previewImage = (e) => {
//         const file = e.target.files[0];
//         if (file) {
//             const reader = new FileReader();
//             reader.onload = function (e) {
//                 setFormData(prev => ({
//                     ...prev,
//                     image: file,
//                     existing_image_url: e.target.result
//                 }));
//             };
//             reader.readAsDataURL(file);
//         } else {
//             setFormData(prev => ({
//                 ...prev,
//                 image: null,
//                 existing_image_url: id && formData.existing_image_url && typeof formData.image === 'string' ? formData.existing_image_url : ""
//             }));
//         }
//         if (formErrors.image) {
//             setFormErrors(prev => ({ ...prev, image: "" }));
//         }
//     };


//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         setSubmitting(true);
//         setFormErrors({});
//         setBackendMessage({ type: '', text: '' });

//         if (!id) {
//             const errors = {};
//             if (!formData.title.trim()) errors.title = "Title is required";
//             // if (!formData.url.trim()) errors.url = "URL is required";
//             if (!formData.image) errors.image = "Please upload an image";
//             if (!formData.status) errors.status = "Status is required";

//             if (Object.keys(errors).length > 0) {
//                 setFormErrors(errors);
//                 // setBackendMessage({ type: 'danger', text: 'Please correct the highlighted errors.' });
//                 setSubmitting(false);
//                 return;
//             }
//         }
//         //md 
//         const dataToSend = new FormData();
//         Object.keys(formData).forEach(key => {
//             if (key === 'existing_image_url') {
//                 // Only include existing_image_url if editing and no new image is uploaded
//                 if (id && !formData.image && formData[key]) {
//                     dataToSend.append('image', formData[key]); // Send as 'image' field so backend recognizes it
//                 }
//                 return;
//             }
//             if (key === 'image' && formData[key]) {
//                 dataToSend.append(key, formData[key]);
//             } else if (key === 'description' && editorInstanceRef.current) {
//                 dataToSend.append(key, editorInstanceRef.current.getData());
//             } else {
//                 dataToSend.append(key, formData[key] === null ? '' : formData[key]);
//             }
//         });

//         try {
//             let response;
//             if (id) {
//                 response = await api.patch(`/bloglist/update/${id}`, dataToSend, {
//                     headers: {
//                         'Content-Type': 'multipart/form-data'
//                     }
//                 });
//                 setBackendMessage({ type: 'success', text: 'Blog add successfully!' });
//             } else {
//                 response = await api.post(`/bloglist/store`, dataToSend, {
//                     headers: {
//                         'Content-Type': 'multipart/form-data'
//                     }
//                 });
//                 setBackendMessage({ type: 'success', text: 'Blog added successfully!' });
//                 setFormData({
//                     title: "",
//                     description: "",
//                     url: "",
//                     status: "active",
//                     image: null,
//                     existing_image_url: "",
//                     tag: "",
//                     short_desc: "",
//                     meta_desc: "",
//                     h1_tag: "",
//                     h2_tag: "",
//                     bold_tag: "",
//                     italic_tag: "",
//                     blog_url: ""
//                 });
//                 if (editorInstanceRef.current) {
//                     editorInstanceRef.current.setData('');
//                 }
//             }
//             setTimeout(() => navigate('/admin/bloglist'), 1500);

//         } catch (err) {
//             console.error("Submission error:", err.response?.data || err.message);
//             if (err.response && err.response.status === 422 && err.response.data.errors) {
//                 const newErrors = {};
//                 err.response.data.errors.forEach(error => {
//                     newErrors[error.param] = error.msg;
//                 });
//                 setFormErrors(newErrors);
//                 setBackendMessage({ type: 'danger', text: 'Validation failed. Please check your inputs.' });
//             } else {
//                 setBackendMessage({ type: 'danger', text: err.response?.data?.error || 'An unexpected error occurred.' });
//             }
//         } finally {
//             setSubmitting(false);
//         }
//     };

//     if (loading) {
//         return (
//             <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s", padding: "20px" }}>
//                 <div className="card">
//                     <div className="card-header">Loading...</div>
//                     <div className="card-body">
//                         <div className="row">
//                             {Array.from({ length: 12 }).map((_, i) => (
//                                 <div key={i} className={`form-group ${i === 0 || i === 1 || i === 2 || i === 3 || i === 4 || i === 5 || i === 7 || i === 8 || i === 9 || i === 10 || i === 11 ? 'col-sm-6' : 'col-sm-12'}`}>
//                                     <div className="skeletant-bx skeletant-design sh-47" style={{ height: '30px', marginBottom: '10px' }}></div>
//                                     <div className="skeletant-bx skeletant-design sh-47" style={{ height: (i === 6 ? '100px' : '40px') }}></div>
//                                 </div>
//                             ))}
//                             <div className="col-sm-12 mt-3">
//                                 <div className="skeletant-bx skeletant-design sh-47" style={{ height: '40px', width: '150px' }}></div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <>
//             <Helmet>
//                 <title>Admin | {id ? "Edit Blog Details" : "Add Blog Details"}</title>
//             </Helmet>
//             <Sidebar isSidebarOpen={isSidebarOpen} />
//             <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
//                 <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
//                     <Header toggleSidebar={toggleSidebar} />
//                     <section className='content'>
//                         <div className="breadcrumb-main-bx">
//                             <div className="breadcrumb-bx">
//                                 <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
//                                 <div className="breadcrumb-link breadcrumb-disabled">/</div>
//                                 <Link className="breadcrumb-link" to="/admin/bloglist">Blog</Link>
//                                 <div className="breadcrumb-link breadcrumb-disabled">/</div>
//                                 <div className="breadcrumb-link breadcrumb-active">{id ? "Edit Blog" : "Add Blog"}</div>
//                             </div>
//                         </div>

//                         <div className="container-fluid">
//                             <div className="row">
//                                 <div className="col-md-12">
//                                     <div className="card">
//                                         <div className="card-header">{id ? "Edit Blog" : "Add Blog"}</div>
//                                         <div className="card-body">
//                                             {backendMessage.text && (
//                                                 <div className={`alert alert-${backendMessage.type === 'success' ? 'success' : 'danger'}`} role="alert">
//                                                     {backendMessage.text}
//                                                 </div>
//                                             )}

//                                             <form onSubmit={handleSubmit} encType="multipart/form-data">
//                                                 <div className="row">
//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="title">Title</label>
//                                                         <input type="text" className={`form-control ${formErrors.title ? 'is-invalid' : ''}`} id="title" placeholder="Enter title" name="title" value={formData.title} onChange={handleChange} />
//                                                         {formErrors.title && <div className="invalid-feedback">{formErrors.title}</div>}
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="url">Url</label>
//                                                         <input type="text" className={`form-control ${formErrors.url ? 'is-invalid' : ''}`} id="url" placeholder="Enter url" name="url" value={formData.url} onChange={handleChange} />
//                                                         {formErrors.url && <div className="invalid-feedback">{formErrors.url}</div>}
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="tag">Meta Keywords</label>
//                                                         <input type="text" className="form-control" id="tag" placeholder="Enter Tag" name="tag" value={formData.tag} onChange={handleChange} />
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="short_desc">Short Description</label>
//                                                         <input type="text" className="form-control" id="short_desc" placeholder="Enter short description" name="short_desc" value={formData.short_desc} onChange={handleChange} />
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="image">Image</label>
//                                                         <input type="file" className={`form-control ${formErrors.image ? 'is-invalid' : ''}`} id="image" name="image" onChange={previewImage} accept="image/jpeg,image/png,image/jpg" />
//                                                         <div className="input-note-2 mt-1">
//                                                             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"></path></svg>
//                                                         </div>
//                                                         {formErrors.image && <div className="invalid-feedback">{formErrors.image}</div>}
//                                                         {formData.existing_image_url && (
//                                                             <img src={buildImageUrl(formData.existing_image_url)} alt="Image Preview" style={{ maxWidth: '100px', marginTop: '10px', display: 'block' }} />
//                                                         )}
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="status">Status</label>
//                                                         <select className={`form-control ${formErrors.status ? 'is-invalid' : ''}`} id="status" name="status" value={formData.status} onChange={handleChange}>
//                                                             <option value="">Select Status</option>
//                                                             <option value="active">Active</option>
//                                                             <option value="inactive">Inactive</option>
//                                                             <option value="draft">Draft</option>
//                                                         </select>
//                                                         {formErrors.status && <div className="invalid-feedback">{formErrors.status}</div>}
//                                                     </div>

//                                                     <div className="form-group col-sm-12">
//                                                         <label htmlFor="description">Description</label>
//                                                         <textarea ref={editorRef} className={`form-control ${formErrors.description ? 'is-invalid' : ''}`} id="description" placeholder="Enter Description" name="description"></textarea>
//                                                         {formErrors.description && <div className="invalid-feedback">{formErrors.description}</div>}
//                                                     </div>

//                                                     <div className="form-group col-sm-12">
//                                                         <label htmlFor="meta_desc">Meta Description</label>
//                                                         <input type="text" className="form-control" id="meta_desc" placeholder="Enter meta description" name="meta_desc" value={formData.meta_desc} onChange={handleChange} />
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="h1_tag">H1 Tags</label>
//                                                         <input type="text" className="form-control" id="h1_tag" placeholder="Enter H1 tags" name="h1_tag" value={formData.h1_tag} onChange={handleChange} />
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="h2_tag">H2 Tags</label>
//                                                         <input type="text" className="form-control" id="h2_tag" placeholder="Enter H2 tags" name="h2_tag" value={formData.h2_tag} onChange={handleChange} />
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="bold_tag">Bold Tags</label>
//                                                         <input type="text" className="form-control" id="bold_tag" placeholder="Enter bold tags" name="bold_tag" value={formData.bold_tag} onChange={handleChange} />
//                                                     </div>

//                                                     <div className="form-group col-sm-6">
//                                                         <label htmlFor="italic_tag">Italic Tags</label>
//                                                         <input type="text" className="form-control" id="italic_tag" placeholder="Enter italic tags" name="italic_tag" value={formData.italic_tag} onChange={handleChange} />
//                                                     </div>

//                                                     {/* <div className="form-group col-sm-6">
//                                                         <label htmlFor="blog_url">Blog URL (Optional)</label>
//                                                         <input type="text" className="form-control" id="blog_url" placeholder="Enter blog URL" name="blog_url" value={formData.blog_url} onChange={handleChange} />
//                                                     </div> */}
//                                                 </div>

//                                                 <button type="submit" className="common-submit-btn mt-2" disabled={submitting}>
//                                                     {submitting ? 'Submitting...' : (id ? 'Update Blog' : 'Add Blog')}
//                                                 </button>
//                                                 {/* <Link to="/admin/bloglist" className="btn btn-secondary ms-2">Cancel</Link> */}
//                                             </form>
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </section>
//                     <Footer />
//                 </div>
//             </div>
//         </>
//     );
// }

// export default AddBlogs;  old 